import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Row,
  useColumnOrder,
  useExpanded,
  useFlexLayout,
  useGroupBy,
  useResizeColumns,
  useRowState,
  useTable,
} from 'react-table';

import { useApprovalFlowContext } from '@mint-lib/approval-flow-circuit';
import { useChannels } from '@mint-lib/hooks';
import { TFunction, useTranslation } from '@mint-lib/i18n';
import {
  useIsAllowedPermission,
  usePermissions,
  useProfile,
} from '@mint-lib/profile-helpers';
import { useFlags } from '@mint-lib/routing-context';
import { getRouteApi } from '@mint/routing';
import { isEmpty } from 'remeda';

import { PanelTab } from '../../../../../../apps/routing/src/routes/_secured/campaign/$campaignId/planning/buying-plan.js';
import { useUpdateMediaPlanRowOldMutation } from '../../../api/mediaPlansApiSlice.js';
import { builderColumnsObjects } from '../../../constants/builderColumnObjects.js';
import {
  BUILDER_ROW_DEPTH,
  BUILDER_ROW_TYPES,
} from '../../../constants/campaignBuilder.js';
import {
  Columns,
  kpiCostPerMetricsToMap,
} from '../../../constants/myCampaigns.js';
import { useBuilderTableCellMutation } from '../../../hooks/useBuilderTableCellMutation.js';
import { useCampaignCurrencyMode } from '../../../hooks/useCampaignCurrencyMode.js';
import { useBuyingPlanTotals } from '../../../hooks/useGetBuyingPlanData.js';
import { useBuyingPlanMetaColumns } from '../../../hooks/useGetBuyingPlanMetaData.js';
import { useGetCampaignData } from '../../../hooks/useGetCampaignData.js';
import { useGetMediaPlanRows } from '../../../hooks/useGetMediaPlanRows.js';
import useGetPlatformIntegrations from '../../../hooks/useGetPlatformIntegrations.js';
import { usePredictiveDataSelector } from '../../../hooks/useGetPredictivePlanningData.js';
import { useIsArchivedVersion } from '../../../hooks/useIsArchivedVersion.js';
import useIsBuyingPlanLocked from '../../../hooks/useIsBuyingPlanLocked.js';
import useLastRowOfChannelIds from '../../../hooks/useLastRowOfChannelIds.js';
import { useTrackingInitiator } from '../../../hooks/usePerformanceTracking.js';
import { useSelectedKPI } from '../../../hooks/usePredictivePlanningMetrics.js';
import { usePredictivePlanningMode } from '../../../hooks/usePredictivePlanningMode.js';
import usePreparedBuilderTableColumns from '../../../hooks/usePreparedBuilderTableColumns.js';
import { useSelectedBuyingPlanId } from '../../../hooks/useSelectedBuyingPlanId.js';
import { useTableScale } from '../../../hooks/useTableScale.js';
import { ColumnItem } from '../../../types/buyingPlan.js';
import { addMetaToRows } from '../../../utils/campaignsBuilder.js';
import { getRowId } from '../../../utils/campaignsTransformer.js';
import {
  BuilderTableRow,
  ColumnOptionItem,
  RowToChangeCurrencyObject,
  RowToRemoveObject,
} from '../types.js';
import SelectTextValue from './BuilderTable/ColumnFields/SelectTextValue/SelectTextValue.jsx';

type SetRowToRemove = Dispatch<SetStateAction<RowToRemoveObject | null>>;
type SetRowToChangeCurrency = Dispatch<
  SetStateAction<RowToChangeCurrencyObject | null>
>;
type BuilderTableArguments = {
  setRowToRemove: SetRowToRemove;
  setRowToChangeCurrency: SetRowToChangeCurrency;
};

const USER_DOES_NOT_USE_APPROVAL_FLOW = null;

const getDefaultTotalRow = (t: TFunction) => ({
  label: t('Totals'),
  name: 'Total',
  id: 'total',
  isCopy: false,
  canBeDeleted: false,
  type: 'total',
  subRows: [],
  parentInfo: {},
  order: 0,
  parent: null,
  subItems: null,
});

const routeApi = getRouteApi(
  '/_secured/campaign/$campaignId/planning/buying-plan/',
);

const useBuilderTable = ({
  setRowToRemove,
  setRowToChangeCurrency,
}: BuilderTableArguments) => {
  const ff = useFlags('@mint/arm', ['mdmColumnsForBuyingPlan']);
  const [expandedType, setExpandedType] = useState(BUILDER_ROW_TYPES.CHANNEL);
  const navigate = routeApi.useNavigate();
  const { tab } = routeApi.useSearch();
  const { t } = useTranslation('@myn/planning');

  const { columnNames, columnObjects } = usePreparedBuilderTableColumns();
  const [selectedKPI] = useSelectedKPI();
  const { data: predictiveData } = usePredictiveDataSelector('mediaRows');
  const { data: predictiveTotals } = usePredictiveDataSelector('totals');

  const preparedRows = useGetMediaPlanRows();

  const totals = useBuyingPlanTotals();
  const mediaPlanMetaColumns = useBuyingPlanMetaColumns();
  const lastRowOfChannelIds = useLastRowOfChannelIds();
  const channelsMeta = useChannels();

  const { integratedPlatforms, platformByName } = useGetPlatformIntegrations();
  const [isCampaignCurrencyMode] = useCampaignCurrencyMode();
  const [isArchivedVersion] = useIsArchivedVersion();
  const isPredictivePlanningMode = usePredictivePlanningMode();
  const isAllowed = useIsAllowedPermission('campaignPlanningFeature:modify');
  const isEditMode = isAllowed && tab === PanelTab.AddRow;
  const scale = useTableScale();
  const startMeasurePerformance = useTrackingInitiator();
  const selectedPlanId = useSelectedBuyingPlanId();

  const { approvalFlowData } = useApprovalFlowContext();
  const profile = useProfile();

  const isBuyingPlanApproved = Boolean(
    approvalFlowData &&
    'approvalItemStatus' in approvalFlowData &&
    (approvalFlowData.approvalItemStatus === 'approved' ||
      approvalFlowData.approvalItemStatus === USER_DOES_NOT_USE_APPROVAL_FLOW),
  );

  const { data: campaign } = useGetCampaignData();
  const [updateMediaPlanRowMutation] = useUpdateMediaPlanRowOldMutation();
  const updateCellMutation = useBuilderTableCellMutation();

  const { permissions } = usePermissions();
  const isNonFinancialUser = !permissions.financialDataFeature;
  const isAdServerCostAllowed = !!permissions.adserverCostFeature;
  const isDspCostAllowed = !!permissions.dpsCostFeature;
  const expandedBy = useRef<BUILDER_ROW_TYPES>(null);
  const { isLocked: isBuyingPlanLocked } = useIsBuyingPlanLocked();

  const searchParams = routeApi.useSearch();
  const chosenRowId = Number(searchParams.row);
  const userIoGroupId = profile.profile?.ioGroupId ?? null;

  const defaultTotalRow = getDefaultTotalRow(t);

  const rowsWithTotal = useMemo(
    () =>
      totals && preparedRows.length
        ? [
            {
              ...defaultTotalRow,
              ...totals,
              ...predictiveTotals,
              currencySign: campaign?.currency?.sign ?? '',
              currencyValue: campaign?.currency?.value ?? null,
            },
            ...preparedRows,
          ]
        : preparedRows,
    [preparedRows, totals, campaign?.currency, predictiveTotals, t],
  );
  const rowsDataWithMeta = useMemo(() => {
    return addMetaToRows(rowsWithTotal, {
      isBuyingPlanLocked,
      isBuyingPlanApproved,
      isCampaignCurrencyMode,
      isPredictivePlanningMode,
      isArchivedVersion,
      integratedPlatforms,
      isEditMode,
      platformByName,
      channelsMeta,
      lastRowOfChannelIds,
      scale,
      selectedPlanId,
      userIoGroupId,
    });
  }, [
    rowsWithTotal,
    isBuyingPlanLocked,
    isBuyingPlanApproved,
    isCampaignCurrencyMode,
    isPredictivePlanningMode,
    isArchivedVersion,
    isEditMode,
    integratedPlatforms,
    platformByName,
    lastRowOfChannelIds,
    scale,
    selectedPlanId,
    userIoGroupId,
  ]);

  const handleChangeColumnVal = useCallback(
    async (
      row: { id: number; type: string },
      field: string,
      value: string | number,
    ) => {
      const currentValue = !value && value !== 0 ? null : value;

      try {
        if (ff.mdmColumnsForBuyingPlan.enabled) {
          await updateCellMutation({
            rowId: row.id,
            ...(field === Columns.EXPANDER
              ? { name: String(value) }
              : { fields: { [field]: currentValue } }),
          });
          return { isError: false };
        }

        await updateMediaPlanRowMutation({
          rowId: row.id,
          ...(field === Columns.EXPANDER
            ? { name: value }
            : { fields: { [field]: currentValue } }),
        }).unwrap();

        return { isError: false };
      } catch {
        return { isError: true };
      }
    },
    [ff.mdmColumnsForBuyingPlan.enabled],
  );

  // TODO: add switch to some places
  const preparedColumns = useMemo(
    () =>
      columnObjects.reduce((acc: any[], column) => {
        const shouldHideForNonFinancialUser =
          isNonFinancialUser &&
          'hideColumnForNonFinancialUser' in column &&
          column.hideColumnForNonFinancialUser;
        const shouldHideAdserverCost =
          column.id === Columns.ADSERVER_COST && !isAdServerCostAllowed;
        const shouldHideDspCost = column.id === 'dspCost' && !isDspCostAllowed;
        const shouldHideForNonIntegratedUser =
          (!isAdServerCostAllowed || !isDspCostAllowed) &&
          'hideForNonIntegratedUser' in column &&
          column.hideForNonIntegratedUser;

        const shouldHideColumn =
          shouldHideForNonFinancialUser ||
          shouldHideAdserverCost ||
          shouldHideDspCost ||
          shouldHideForNonIntegratedUser;

        if (shouldHideColumn) {
          return acc;
        }

        const updatedColumn = {
          ...column,
          isCalculatedTechCostColumn: false,
          updateRow: handleChangeColumnVal,
          startMeasurePerformance,
          campaign,
        };

        if (column.id === Columns.EXPANDER) {
          const [firstRow, ...otherRows] = preparedRows;
          const isInitialOpened = !otherRows.length && isEmpty(firstRow);

          return [
            ...acc,
            {
              ...updatedColumn,
              isInitialOpened,
            },
          ];
        }

        if (column.id === Columns.ACTIONS_MENU) {
          return [
            ...acc,
            {
              ...updatedColumn,
              onRemoveClick: setRowToRemove,
              onChangeRowCurrencyClick: setRowToChangeCurrency,
              navigate,
            },
          ];
        }

        if (column.id === Columns.BUDGET && predictiveData && selectedKPI) {
          const currentCostPerMetric =
            kpiCostPerMetricsToMap[
              selectedKPI as keyof typeof kpiCostPerMetricsToMap
            ] || kpiCostPerMetricsToMap.default;

          const currentPredictiveCostPerMetricColumn = {
            ...builderColumnsObjects[Columns.COST_PER_METRIC],
            Header: builderColumnsObjects[currentCostPerMetric].Header,
          };

          return [
            ...acc,
            updatedColumn,
            builderColumnsObjects.optimalBudget,
            currentPredictiveCostPerMetricColumn,
          ];
        }

        const columnMeta = mediaPlanMetaColumns?.[column?.id ?? ''];

        if (!columnMeta) {
          return [...acc, updatedColumn];
        }

        switch (column.id) {
          case Columns.PARTNER: {
            const options = integratedPlatforms
              .map(({ platform, label }) => ({
                value: platform,
                displayName: label,
              }))
              .sort((a: ColumnOptionItem, b: ColumnOptionItem) =>
                a.value.localeCompare(b.value),
              );

            return [
              ...acc,
              {
                ...updatedColumn,
                options,
              },
            ];
          }

          case Columns.ITEM_GOAL: {
            return [
              ...acc,
              {
                ...updatedColumn,
                columnMeta,
              },
            ];
          }

          case Columns.FUNNEL_STRATEGY: {
            const options = mediaPlanMetaColumns.funnelStrategy.map((name) => ({
              value: name,
              displayName: `${name[0].toUpperCase()}${name.substring(1)}`,
            }));

            return [
              ...acc,
              {
                ...updatedColumn,
                options,
              },
            ];
          }

          case Columns.ACTIVE_DAYS: {
            const options = columnMeta.map(
              ({ name, label }: { name: string; label: string }) => ({
                value: name,
                displayName: label,
              }),
            );

            return [
              ...acc,
              {
                ...updatedColumn,
                options,
              },
            ];
          }
        }

        const options = columnMeta.map(
          ({ name, label, isDeleted }: ColumnItem) => ({
            value: name,
            displayName: label,
            isDeleted,
          }),
        );

        return [
          ...acc,
          {
            ...updatedColumn,
            options,
            Cell: SelectTextValue,
          },
        ];
      }, []),
    [
      preparedRows,
      mediaPlanMetaColumns,
      campaign,
      predictiveData,
      isNonFinancialUser,
    ],
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    columns,
    prepareRow,
    setColumnOrder,
    toggleRowExpanded,
    toggleAllRowsExpanded,
    flatRows,
    expandedRows,
    setRowState,
    setCellState,
    state: { columnResizing },
  }: any = useTable(
    {
      columns: preparedColumns,
      data: rowsDataWithMeta,
      //@ts-ignore
      getRowId,
      initialState: {
        //@ts-ignore
        columnOrder: columnNames,
      },
      autoResetExpanded: false,
      autoResetRowState: false,
    },
    useColumnOrder,
    useGroupBy,
    useExpanded,
    useResizeColumns,
    useFlexLayout,
    useRowState,
  );

  const expandRowsByType = useCallback(
    (type: BUILDER_ROW_TYPES) => {
      expandedBy.current = type;
      if (type === BUILDER_ROW_TYPES.TILE) {
        toggleAllRowsExpanded(true);
      }

      if (type === BUILDER_ROW_TYPES.CHANNEL) {
        toggleAllRowsExpanded(false);
      }

      if (type === BUILDER_ROW_TYPES.PLATFORM) {
        flatRows.forEach((row: Row<{ type: string }>) =>
          row.original.type === BUILDER_ROW_TYPES.CHANNEL
            ? toggleRowExpanded(row.id, true)
            : toggleRowExpanded(row.id, false),
        );
      }
    },
    [flatRows, toggleAllRowsExpanded, toggleRowExpanded],
  );

  useEffect(() => {
    const channels = flatRows.filter(
      (row: BuilderTableRow) =>
        row.depth === BUILDER_ROW_DEPTH.CHANNEL &&
        row.original.type !== BUILDER_ROW_TYPES.TOTAL,
    );

    const expandedChannels = expandedRows.filter(
      (row: BuilderTableRow) =>
        row.depth === 0 &&
        row.isExpanded &&
        row.original.type !== BUILDER_ROW_TYPES.TOTAL,
    );

    const platforms = flatRows.filter(
      (row: BuilderTableRow) => row.depth === BUILDER_ROW_DEPTH.PLATFORM,
    );

    const expandedPlatforms = expandedRows.filter(
      (row: BuilderTableRow) =>
        row.depth === BUILDER_ROW_DEPTH.PLATFORM && row.isExpanded,
    );

    const isAllChannelsExpanded = expandedChannels.length === channels.length;
    const isAllChannelsAndPlatformsExpanded =
      channels.length === expandedChannels.length &&
      platforms.length === expandedPlatforms.length;

    if (!isAllChannelsExpanded && !expandedChannels.length) {
      setExpandedType(BUILDER_ROW_TYPES.CHANNEL);
    }
    if (
      isAllChannelsExpanded &&
      !expandedPlatforms.length &&
      !expandedChannels.length
    ) {
      setExpandedType(BUILDER_ROW_TYPES.PLATFORM);
    }
    if (
      isAllChannelsAndPlatformsExpanded &&
      !expandedPlatforms.length &&
      !expandedChannels.length
    ) {
      setExpandedType(BUILDER_ROW_TYPES.TILE);
    }
  }, [expandedRows, flatRows]);

  useEffect(() => {
    const isAnyRowExpanded = expandedRows.some(
      (row: BuilderTableRow) => row.isExpanded,
    );
    if (flatRows.length && !isAnyRowExpanded && !chosenRowId) {
      setExpandedType(BUILDER_ROW_TYPES.CHANNEL);
      expandRowsByType(BUILDER_ROW_TYPES.CHANNEL);
    }
    if (chosenRowId && !isAnyRowExpanded) {
      const chosenRow = flatRows.find((r: BuilderTableRow) =>
        r.original.subRows.find((subRow: any) => subRow.id === chosenRowId),
      );

      const isChosenRowPlatform =
        chosenRow?.original.type === BUILDER_ROW_TYPES.PLATFORM ||
        chosenRow?.original.type === BUILDER_ROW_TYPES.MEDIA_NETWORK;

      if (isChosenRowPlatform) {
        const channelId = chosenRow.original.parent;
        const platformId = chosenRow.id;

        toggleRowExpanded(channelId, true);
        toggleRowExpanded(platformId, true);
      }
    }
  }, [flatRows, chosenRowId]);

  useEffect(() => {
    if (expandedType === BUILDER_ROW_TYPES.TILE) {
      return toggleAllRowsExpanded(true);
    }

    if (expandedBy.current !== expandedType) {
      expandRowsByType(expandedType);
    }
  }, [expandedType]);

  useEffect(() => {
    setColumnOrder(columnNames);
  }, [setColumnOrder, columnNames]);

  return useMemo(
    () => ({
      getTableProps,
      getTableBodyProps,
      headerGroups,
      rows,
      prepareRow,
      setColumnOrder,
      toggleRowExpanded,
      columnResizing,
      columns,
      expandRowsByType,
      expandedType,
      setExpandedType,
      setRowState,
      setCellState,
      startMeasurePerformance,
    }),
    [
      getTableProps,
      getTableBodyProps,
      headerGroups,
      rows,
      prepareRow,
      setColumnOrder,
      toggleRowExpanded,
      columnResizing,
      columns,
      expandRowsByType,
      expandedType,
      setExpandedType,
      setRowState,
      setCellState,
      startMeasurePerformance,
    ],
  );
};

export default useBuilderTable;
